
import { 
  BarChart, 
  Users, 
  DollarSign, 
  CheckCircle, 
  TrendingUp, 
  Target, 
  Zap, 
  Shield 
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

const FeatureCard = ({ 
  icon: Icon, 
  title, 
  description, 
  delay = 0,
  color = "woost-soft-blue",
  iconColor = "woost-blue"
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            setIsVisible(true);
          }, delay);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [delay]);

  return (
    <motion.div 
      ref={ref}
      className={`bg-white rounded-xl p-4 shadow-md border border-gray-100 transition-all duration-500 ${
        isVisible 
          ? 'opacity-100 translate-y-0' 
          : 'opacity-0 translate-y-10'
      }`}
      whileHover={{ 
        y: -5,
        boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
        transition: { duration: 0.2 }
      }}
    >
      <div className='md:flex items-center gap-3'>
      <div className={`bg-${color} p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4`}>
        <Icon className={`text-${iconColor}`} size={24} />
      </div>
      <h3 className="text-lg font-semibold mb-2">{title}</h3>
      </div>
      <p className="text-gray-600 hidden sm:block">{description}</p>
    </motion.div>
  );
};

const DifferenceSection = () => {
  const features = [
    {
      icon: BarChart,
      title: "ROI-First Approach",
      description: "We prioritize measurable outcomes over vanity metrics",
      color: "woost-soft-blue",
      iconColor: "woost-blue"
    },
    {
      icon: Users,
      title: "Nano & Micro Influencers",
      description: "Higher engagement rates with authentic audience connections",
      color: "woost-soft-yellow",
      iconColor: "woost-yellow"
    },
    {
      icon: DollarSign,
      title: "Pay Only for Performance",
      description: "Results-based model ensures you get what you pay for",
      color: "woost-soft-green",
      iconColor: "green-600"
    },
    {
      icon: CheckCircle,
      title: "Trusted by Top Brands",
      description: "Major brands rely on Woost for consistent results",
      color: "woost-soft-pink",
      iconColor: "pink-500"
    },
    // {
    //   icon: TrendingUp,
    //   title: "Rapid Growth",
    //   description: "Scale your social presence quickly with our massive creator network",
    //   color: "purple-100",
    //   iconColor: "woost-purple"
    // },
    // {
    //   icon: Zap,
    //   title: "Quick Turnaround",
    //   description: "Get content produced and published in record time",
    //   color: "amber-100",
    //   iconColor: "amber-500"
    // },
    // {
    //   icon: Target,
    //   title: "Precise Targeting",
    //   description: "Reach exactly the right audience for your product",
    //   color: "sky-100",
    //   iconColor: "sky-500"
    // },
    // {
    //   icon: Shield,
    //   title: "Quality Assurance",
    //   description: "All content is pre-approved by your team before going live",
    //   color: "teal-100",
    //   iconColor: "teal-500"
    // }
  ];

  return (
    <section className="py-20 bg-gray-50" style={{paddingBottom: "50px"}}>
      <div className="container mx-auto px-4 md:px-6">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">What Makes Woost Different?</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We focus on delivering real results with nano and micro influencers
          </p>
        </motion.div>
        
        <div className="relative overflow-x-auto -mx-4 px-4 md:overflow-visible md:px-0">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-6 mb-2 snap-x snap-mandatory scroll-pl-4 md:scroll-pl-0">
            {features.slice(0, 4).map((feature, index) => (
              <div key={index} className="w-[280px] flex-shrink-0 md:w-auto snap-start">
                <FeatureCard
                  icon={feature.icon}
                  title={feature.title}
                  description={feature.description}
                  delay={index * 100}
                  color={feature.color}
                  iconColor={feature.iconColor}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DifferenceSection;
